import filter from 'lodash/filter';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mergeWith from 'lodash/mergeWith';
import values from 'lodash/values';

import { moment } from '@eventbrite/date';
import momentTimezone from 'moment-timezone';

import {
    getDateForAttendee,
    getAttendeeTimezone,
} from '@eventbrite/event-date-time';

import { EVENT_SERIES_FORM_NAME } from '../constants';
import { EventStatus } from '../constants/eventStatus';
import { createSelector } from './utils/selector';
import { getIsEventOnline, isSeries } from './event';
import { getIsEnableTimedEntryFF, isEventbriteTLD } from './app';

export const getSeriesIsLoading = (state) =>
    get(state, 'series.isLoading', false);

export const getSeriesEvents = (state) => get(state, 'series.seriesEvents');

export const getSeriesId = (state) => get(state, 'series.seriesId');

export const getPreloadedSeriesEvents = (state) =>
    get(state, 'app.event.series_dates');

export const getSelectedDate = (state) => {
    const selectedDate = get(state, [
        'form',
        EVENT_SERIES_FORM_NAME,
        'values',
        'filterDate',
    ]);

    if (!selectedDate) return;
    return getUtcDate(selectedDate, state);
};

/**
 * Receives a date YYYY-DD-MM and returns utc date
 * For online events it will return the beginning of the day using
 * attendee's timezone
 * For onsite events it will return the beginning of the day using
 * creator's timezone
 */
const getUtcDate = (date, state) => {
    const isEventOnline = getIsEventOnline(state);
    const creatorTimezone = get(state, 'event.start.timezone');
    const timezone = isEventOnline
        ? getAttendeeTimezone() ?? creatorTimezone
        : creatorTimezone;
    return momentTimezone.tz(date, timezone).toISOString();
};

/**
 * Gets all the preloaded event dates from the app context and
 * transforms them into a event id => event map to match the shape
 * under state.series.seriesEvents so it's easier to merge both.
 */
export const getPreloadedSeriesEventsById = createSelector(
    getPreloadedSeriesEvents,
    (preloadedSeriesEvents) => keyBy(preloadedSeriesEvents, 'id'),
);

const seriesEventsMerge = (current, payload) => ({
    ...current,
    ...payload,
});

/**
 * Utility to deep merge two id=>event maps.
 */
export const mergeSeriesEvents = (...events) =>
    mergeWith({}, ...events, seriesEventsMerge);

/**
 * Gets preloaded and api series events into a unified map.
 */
export const getSeriesEventsWithPreloaded = createSelector(
    getPreloadedSeriesEventsById,
    getSeriesEvents,
    mergeSeriesEvents,
);

const hasSomeAvailableEvent = (eventInfo) =>
    eventInfo.some(
        (event) =>
            (!event.soldOut || event.hasWaitlistAvailable) &&
            event.status !== EventStatus.canceled,
    );

/**
 * Generates a calendar structure from the event dates list
 * ```js
 * {
 *    'YYYY-MM-DD': [
 *        { date: 'YYYY-MM-DD', soldOut: Boolean },
 *        { date: 'YYYY-MM-DD', soldOut: Boolean },
 *     ]
 * }
 * ```
 */
export const getSeriesCalendar = createSelector(
    getIsEventOnline,
    getSeriesEventsWithPreloaded,
    (isOnlineEvent, seriesEvents) => {
        const groupedDates = groupBy(
            map(seriesEvents, (event) => ({
                status: event.status,
                date: getDateForAttendee({
                    isOnlineEvent,
                    dateAndTimezone: event.start,
                }).format('YYYY-MM-DD'),
                eventStart: event.start,
                soldOut: get(event, 'ticketAvailability.isSoldOut', false),
                hasWaitlistAvailable: get(
                    event,
                    'ticketAvailability.waitlistAvailable',
                    false,
                ),
            })),
            'date',
        );

        return Object.entries(groupedDates).reduce(
            (eventList, [, events]) =>
                hasSomeAvailableEvent(events)
                    ? [
                          ...eventList,
                          {
                              date: new Date(events[0].eventStart.utc),
                          },
                      ]
                    : eventList,
            [],
        );
    },
);

/*
    Returns the events for the day passed as 'date'.
*/
const getEventsOnDate = (date, events) => {
    // Calculate next day so we can search in the range.
    // Date is given in utc, as it is not enough with YYYY-MMM-DD
    const nextDay = moment(date).add(1, 'days');
    return filter(
        events,
        (event) =>
            moment(event.start.utc).isSameOrAfter(date) &&
            moment(event.start.utc).isBefore(nextDay),
    );
};

/**
 * Generates an array of events and filters by the current selected date in redux-form.
 * If there's no selected date, returns all currently loaded events.
 */
export const getSeriesEventsFiltered = createSelector(
    getSelectedDate,
    getSeriesEvents,
    (selectedDate, seriesEvents = []) =>
        selectedDate
            ? getEventsOnDate(selectedDate, seriesEvents)
            : values(seriesEvents),
);

/**
 * Gets filtered events and sorts them by startDate.
 * Events with the same startDate are then sorted by endDate.
 */
export const getSeriesEventsFilteredAndSortedByStartDate = createSelector(
    getSeriesEventsFiltered,
    (seriesEvents = []) =>
        seriesEvents.concat().sort((eventA, eventB) => {
            const startA = eventA.start.utc;
            const startB = eventB.start.utc;

            return moment(startA).isAfter(startB, 'm') ||
                (moment(startA).isSame(startB, 'm') &&
                    moment(eventA.end.utc).isAfter(eventB.end.utc, 'm'))
                ? 1
                : -1;
        }),
);

const getCalendarDatesBetween = (seriesEvents, from, to) =>
    filter(
        seriesEvents,
        (calendarDay) =>
            moment(calendarDay.start.utc).isSameOrAfter(from) &&
            moment(calendarDay.start.utc).isSameOrBefore(to),
    );

/**
 * Returns Boolean indicating if all events happening in between a given start
 * date range currently have availability data loaded (event.ticketAvailability prop is defined).
 *
 * If there are no events happening in between the two provided dates, returns true.
 */
export const getIsEventDateAvailabilityLoaded = createSelector(
    (_, props) => moment(props.startDateFrom).format('YYYY-MM-DD'),
    (_, props) => moment(props.startDateTo).format('YYYY-MM-DD'),
    getSeriesEventsWithPreloaded,
    (startDateFrom, startDateTo, seriesEvents) => {
        const eventsOnDates = getCalendarDatesBetween(
            seriesEvents,
            startDateFrom,
            startDateTo,
        );

        // NOTE: .every returns true for an empty array.
        return eventsOnDates.every(
            (event) => event && event.ticketAvailability,
        );
    },
);

export const getIsAnyAvailabilityLoaded = createSelector(
    getSeriesEvents,
    (seriesEvents) =>
        Boolean(seriesEvents) && Object.keys(seriesEvents).length > 0,
);

export const getSelectedDateWithSession = ({ series }) =>
    series.selectedDateWithSession;

export const getIsTimedEntry = (state) =>
    getIsEnableTimedEntryFF(state) && isSeries(state) && isEventbriteTLD(state);
